import { ArrowLeft, ArrowRight, Check, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/vmemoo/Reveal";

function slugify(value) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function linkSlug(value) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function TextBlock({ text, context = "document" }) {
  const urlPattern = /(https:\/\/[^\s]+|\/(?:privacy|terms|community-guidelines|cookie-data-use|memorial-policy|copyright|safety|account-deletion|privacy-request|contact)(?=[\s.,]|$)|[\w.+-]+@[\w.-]+\.[A-Za-z]{2,})/g;
  const chunks = text.split(urlPattern);
  return (
    <>
      {chunks.map((chunk, index) => {
        if (/^https:\/\//.test(chunk)) return <a key={`${chunk}-${index}`} data-testid={`legal-${context}-${linkSlug(chunk)}-${index}`} href={chunk}>{chunk}</a>;
        if (/^\//.test(chunk)) return <a key={`${chunk}-${index}`} data-testid={`legal-${context}-${linkSlug(chunk)}-${index}`} href={chunk}>{chunk}</a>;
        if (/^[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(chunk)) return <a key={`${chunk}-${index}`} data-testid={`legal-${context}-${linkSlug(chunk)}-${index}`} href={`mailto:${chunk}`}>{chunk}</a>;
        return <span key={`${chunk}-${index}`}>{chunk}</span>;
      })}
    </>
  );
}

function LegalSection({ section, index }) {
  const id = section.id || slugify(section.heading);
  return (
    <Reveal className="legal-section-wrap" delay={Math.min(index * 30, 240)}>
      <section id={id} className="legal-section" data-testid={`legal-section-${id}`}>
        <h2>{section.heading}</h2>
        {section.paragraphs?.map((paragraph, paragraphIndex) => (
          <p key={`${id}-p-${paragraphIndex}`}><TextBlock text={paragraph} context={`${id}-p-${paragraphIndex}`} /></p>
        ))}
        {section.bullets && (
          <ul>
            {section.bullets.map((bullet, bulletIndex) => <li key={`${id}-b-${bulletIndex}`}><TextBlock text={bullet} context={`${id}-b-${bulletIndex}`} /></li>)}
          </ul>
        )}
        {section.numbered && (
          <ol>
            {section.numbered.map((item, itemIndex) => <li key={`${id}-n-${itemIndex}`}><TextBlock text={item} context={`${id}-n-${itemIndex}`} /></li>)}
          </ol>
        )}
      </section>
    </Reveal>
  );
}

export function LegalPage({ document }) {
  return (
    <main className="legal-page" data-testid={`legal-page-${document.slug}`}>
      <div className="legal-masthead">
        <div className="legal-masthead-inner">
          <p className="eyebrow eyebrow-amber">Vmemoo / Public document</p>
          <h1>{document.title}</h1>
          <p className="legal-meta">{document.kind || "Public document"} · Version {document.version} · Effective {document.effectiveDate}</p>
        </div>
      </div>
      <div className="legal-content-wrap">
        <aside className="legal-toc" aria-label="On this page">
          <p className="toc-label">On this page</p>
          <nav>
            {document.sections.map((section) => {
              const id = section.id || slugify(section.heading);
              return <a key={id} href={`#${id}`} data-testid={`legal-toc-${id}-link`}>{section.heading}</a>;
            })}
          </nav>
        </aside>
        <article className="legal-copy" data-testid="legal-document-content">
          {document.intro && <p className="legal-intro"><TextBlock text={document.intro} context={`${document.slug}-intro`} /></p>}
          {document.sections.map((section, index) => <LegalSection key={section.heading} section={section} index={index} />)}
          <div className="legal-endnote">
            <ShieldCheck size={18} aria-hidden="true" />
            <span>Vmemoo Limited · Version {document.version} · Effective {document.effectiveDate}</span>
          </div>
        </article>
      </div>
    </main>
  );
}

export function LegalBackLink() {
  return <Link className="legal-back-link" to="/"><ArrowLeft size={15} aria-hidden="true" /> Back to Vmemoo</Link>;
}

export function LegalPageControls() {
  return <div className="legal-controls" aria-label="Document controls"><a href="#top" data-testid="legal-back-to-top-link"><ArrowRight size={14} aria-hidden="true" /> Top</a></div>;
}
